import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function PhilosophySection() {
  const { t } = useTranslation("home");
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="philosophy"
      ref={ref}
      style={{ background: "#FAF5E8", padding: "100px 0" }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section label */}
        <div
          className="flex items-center gap-3 mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transition: "opacity 0.8s ease",
          }}
        >
          <div style={{ width: 3, height: 24, background: "#2C5F4F", borderRadius: 2 }} />
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.14em",
              color: "#2C5F4F",
              textTransform: "uppercase",
            }}
          >
            {t("philosophy.label")}
          </span>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-stretch">
          {/* Left: Green Panel */}
          <div
            style={{
              flex: "0 0 auto",
              width: "100%",
              maxWidth: 340,
              borderRadius: 24,
              background: "linear-gradient(135deg, #2C5F4F 0%, #3A7A63 100%)",
              padding: "52px 44px",
              position: "relative",
              overflow: "hidden",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-24px)",
              transition: "opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s",
            }}
          >
            {/* Decorative circle */}
            <div
              style={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 220,
                height: 220,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.04)",
              }}
            />

            <p
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.14em",
                color: "rgba(255,255,255,0.6)",
                textTransform: "uppercase",
                marginBottom: 24,
              }}
            >
              {t("philosophy.visionLabel")}
            </p>
            <div
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 52,
                color: "rgba(255,255,255,0.15)",
                lineHeight: 1,
                marginBottom: 20,
                fontWeight: 900,
              }}
            >
              &ldquo;
            </div>
            <p
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 18,
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.8,
              }}
            >
              {t("philosophy.visionQuote")}
            </p>
            <div
              style={{
                width: 40,
                height: 2,
                background: "rgba(255,255,255,0.4)",
                borderRadius: 2,
                marginTop: 28,
              }}
            />
          </div>

          {/* Right: Text */}
          <div
            style={{
              flex: 1,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(24px)",
              transition: "opacity 0.9s ease 0.35s, transform 0.9s ease 0.35s",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingLeft: 8,
            }}
          >
            <h2
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 800,
                fontSize: "clamp(22px, 2.8vw, 32px)",
                color: "#1A1A1A",
                lineHeight: 1.5,
                marginBottom: 24,
              }}
            >
              {t("philosophy.heading")}
            </h2>

            <p
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 15,
                lineHeight: 2.1,
                color: "#4A4A4A",
                maxWidth: 560,
                marginBottom: 24,
              }}
            >
              {t("philosophy.paragraph1")}
            </p>

            <p
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 15,
                lineHeight: 2.1,
                color: "#4A4A4A",
                maxWidth: 560,
                marginBottom: 36,
              }}
            >
              {t("philosophy.paragraph2Before")}
              <strong style={{ color: "#2C5F4F", fontWeight: 700 }}>
                {t("philosophy.paragraph2Highlight")}
              </strong>
              {t("philosophy.paragraph2After")}
            </p>

            {/* Values */}
            <div className="flex flex-wrap gap-3">
              {[
                t("philosophy.values.independence"),
                t("philosophy.values.coCreation"),
                t("philosophy.values.expertise"),
                t("philosophy.values.evidence"),
                t("philosophy.values.diversity"),
              ].map((v) => (
                <span
                  key={v}
                  style={{
                    padding: "6px 16px",
                    borderRadius: 999,
                    border: "1.5px solid rgba(180,150,90,0.3)",
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#2C5F4F",
                    background: "rgba(245,237,214,0.5)",
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
